import styled from 'styled-components';
import { breakpoints, theme as appTheme } from 'theme';

export type FileKind =
  | 'pdf'
  | 'image'
  | 'doc'
  | 'sheet'
  | 'slides'
  | 'archive'
  | 'other';

const getBadgeColor = (kind: FileKind, theme: typeof appTheme) => {
  switch (kind) {
    case 'pdf':
      return theme.color.bittersweet;
    case 'image':
      return theme.color.activeBlue;
    case 'doc':
      return theme.color.BlueBayoux;
    case 'sheet':
      return theme.color.skobeloffGreen;
    case 'slides':
      return theme.color.koromiko;
    case 'archive':
      return theme.color.baliHai;
    default:
      return theme.color.brightTurquoise;
  }
};

export const StyledAttachments = styled.div`
  margin-top: 24px;
`;

export const StyledAttachmentsHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 12px;
`;

export const StyledAttachmentsTitle = styled.span`
  font-size: 14px;
  color: ${({ theme }) => theme.color.baliHai};
`;

export const StyledUsage = styled.span<{ overLimit?: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-size: 12px;
  color: ${({ overLimit, theme }) =>
    overLimit ? theme.color.bittersweet : theme.color.baliHai};
  white-space: nowrap;
`;

export const StyledUsageBar = styled.span<{
  progress: number;
  overLimit?: boolean;
}>`
  position: relative;
  width: 56px;
  height: 4px;
  border-radius: 2px;
  overflow: hidden;
  background: ${({ theme }) => theme.color.blackSqueeze};

  &::after {
    content: '';
    position: absolute;
    inset: 0 auto 0 0;
    width: ${({ progress }) => Math.round(progress * 100)}%;
    border-radius: 2px;
    background: ${({ overLimit, theme }) =>
      overLimit ? theme.color.bittersweet : theme.color.brightTurquoise};
    transition: width 0.2s ease;
  }

  @media ${breakpoints.downSmPlus} {
    display: none;
  }
`;

export const StyledAttachmentsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(210px, 1fr));
  gap: 10px;

  @media ${breakpoints.downSmPlus} {
    grid-template-columns: 1fr;
  }
`;

export const StyledAttachmentRemove = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 28px;
  height: 28px;
  padding: 0;
  border: none;
  border-radius: 8px;
  background: transparent;
  color: ${({ theme }) => theme.color.baliHai};
  cursor: pointer;
  opacity: 0;
  transition: opacity 0.12s ease, background 0.12s ease, color 0.12s ease;

  svg {
    width: 14px;
    height: 14px;
  }

  &:hover,
  &:focus-visible {
    outline: none;
    background: ${({ theme }) => theme.color.blackSqueeze};
    color: ${({ theme }) => theme.color.bittersweet};
  }

  @media (hover: none) {
    opacity: 1;
  }
`;

export const StyledAttachmentCard = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 0;
  padding: 10px 8px 10px 10px;
  border-radius: 12px;
  border: 1px solid ${({ theme }) => theme.color.porcelain};
  background: ${({ theme }) => theme.color.white};
  box-shadow: 0 1px 3px ${({ theme }) => `${theme.color.pickledBluewood}12`};
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
  animation: attachmentIn 0.18s ease-out;

  &:hover,
  &:focus-within {
    border-color: ${({ theme }) => theme.color.botticelli};
    box-shadow: 0 6px 18px -8px ${({ theme }) => `${theme.color.pickledBluewood}40`};
  }

  &:hover ${StyledAttachmentRemove}, &:focus-within ${StyledAttachmentRemove} {
    opacity: 1;
  }

  @keyframes attachmentIn {
    from {
      opacity: 0;
      transform: translateY(4px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;

export const StyledFileIcon = styled.span`
  position: relative;
  display: inline-flex;
  flex-shrink: 0;
  width: 34px;
  height: 40px;
  border-radius: 4px 10px 4px 4px;
  border: 1px solid ${({ theme }) => theme.color.porcelain};
  background: ${({ theme }) => theme.color.aquaSqueeze};
`;

export const StyledFileBadge = styled.span<{ kind: FileKind }>`
  position: absolute;
  left: -6px;
  bottom: 6px;
  padding: 2px 4px;
  border-radius: 4px;
  font-size: 9px;
  font-weight: 800;
  letter-spacing: 0.02em;
  line-height: 1.1;
  color: ${({ theme }) => theme.color.white};
  background: ${({ kind, theme }) => getBadgeColor(kind, theme)};
`;

export const StyledAttachmentThumb = styled.img`
  flex-shrink: 0;
  width: 40px;
  height: 40px;
  border-radius: 8px;
  object-fit: cover;
  border: 1px solid ${({ theme }) => theme.color.porcelain};
`;

export const StyledAttachmentInfo = styled.span`
  display: flex;
  flex: 1;
  flex-direction: column;
  min-width: 0;
`;

export const StyledAttachmentName = styled.span`
  font-size: 14px;
  font-weight: 600;
  color: ${({ theme }) => theme.color.shark};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const StyledAttachmentMeta = styled.span`
  margin-top: 2px;
  font-size: 12px;
  color: ${({ theme }) => theme.color.baliHai};
`;

export const StyledError = styled.div`
  margin-top: 8px;
  font-size: 12px;
  color: ${({ theme }) => theme.color.bittersweet};
`;
